import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

const STORAGE_KEY = 'vent_scroll_positions';
const MAX_ENTRIES = 50;
const RESTORE_TIMEOUT_MS = 1000;

type PositionMap = Record<string, number>;

const readPositions = (): PositionMap => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as PositionMap) : {};
  } catch {
    return {};
  }
};

const writePositions = (positions: PositionMap) => {
  try {
    const keys = Object.keys(positions);
    if (keys.length > MAX_ENTRIES) {
      // Drop the oldest inserted keys (object key order preserves insertion).
      keys.slice(0, keys.length - MAX_ENTRIES).forEach(k => delete positions[k]);
    }
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(positions));
  } catch {
    // Storage unavailable (private mode / quota) — restoration silently degrades.
  }
};

/**
 * Remembers window scroll position per history entry and restores it on
 * back/forward navigations. Forward navigations to a new page start at the top.
 * Mount once inside <BrowserRouter>.
 */
const ScrollRestoration = () => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const prevKeyRef = useRef<string | null>(null);

  // Take over from the browser's own (unreliable in SPAs) restoration.
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      const previous = window.history.scrollRestoration;
      window.history.scrollRestoration = 'manual';
      return () => {
        window.history.scrollRestoration = previous;
      };
    }
  }, []);

  // Persist the current position before the tab goes away.
  useEffect(() => {
    const save = () => {
      const key = prevKeyRef.current;
      if (!key) return;
      const positions = readPositions();
      positions[key] = window.scrollY;
      writePositions(positions);
    };
    window.addEventListener('beforeunload', save);
    window.addEventListener('pagehide', save);
    return () => {
      window.removeEventListener('beforeunload', save);
      window.removeEventListener('pagehide', save);
    };
  }, []);

  useEffect(() => {
    const currentKey = location.key;
    const previousKey = prevKeyRef.current;

    // Save where we were on the page we just left.
    if (previousKey && previousKey !== currentKey) {
      const positions = readPositions();
      positions[previousKey] = window.scrollY;
      writePositions(positions);
    }
    prevKeyRef.current = currentKey;

    if (navigationType !== 'POP') {
      window.scrollTo(0, 0);
      return;
    }

    const target = readPositions()[currentKey];
    if (!target) {
      window.scrollTo(0, 0);
      return;
    }

    // Content often loads after mount — retry until the page is tall enough.
    let frame = 0;
    let cancelled = false;
    const start = performance.now();

    const attempt = () => {
      if (cancelled) return;
      const maxScroll =
        document.documentElement.scrollHeight - window.innerHeight;
      if (maxScroll >= target) {
        window.scrollTo(0, target);
        return;
      }
      window.scrollTo(0, Math.max(0, maxScroll));
      if (performance.now() - start < RESTORE_TIMEOUT_MS) {
        frame = requestAnimationFrame(attempt);
      }
    };
    frame = requestAnimationFrame(attempt);

    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
    };
  }, [location.key, navigationType]);

  return null;
};

export default ScrollRestoration;
